import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, TextField, Typography, Link as MuiLink, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { validatePhoneNumber, canEditField} from '../hartUtils';
import StatusField from './StatusField';

// INITIAL FORM DATA - Default values for creating a new client
export const clientInitialData = {
  name: '',
  org_id: '',
  org_address: '',
  org_website: '',
  org_phone: '',
  sector: '',
  status: 'Active',
  inactive: false,
  year_started: '',
  parent_client: '',
};

// FORM VALIDATION
export function validateClient(data) {
  const errors = {};

  // Name is required (it's the primary key)
  if (!data.name || !data.name.trim()) {
    errors.name = 'Organization Name is required';
  }

  // Validate URL format for org_website if provided
  if (data.org_website && data.org_website.trim()) {
    try {
      new URL(data.org_website);
    } catch (e) {
      errors.org_website = 'Please enter a valid URL (e.g., https://example.com)';
    }
  }

  // Validate year_started if provided
  if (data.year_started) {
    const year = parseInt(data.year_started, 10);
    const currentYear = new Date().getFullYear();
    if (isNaN(year) || year < 1900 || year > currentYear + 1) {
      errors.year_started = `Please enter a valid year between 1900 and ${currentYear + 1}`;
    }
  }

  const phoneError = validatePhoneNumber(data.org_phone);
  if (phoneError) errors.org_phone = phoneError;

  return errors;
}

// NULLABLE FIELDS - Fields that should be converted from empty string to null
export const clientNullableFields = [
  'org_id',
  'org_address',
  'org_website',
  'sector',
  'year_started',
  'parent_client',
];

export const clientPhoneFields = [
  'org_phone',
];

// Simple FormField that handles both view and edit modes
const FormField = ({ label, value, editable, onChange, error, helperText, required, type, multiline, minRows, maxRows, isLink, formatValue }) => {
  if (!editable) {
    // View mode
    const displayValue = formatValue ? formatValue(value) : (value || '—');

    if (multiline) {
      return (
        <>
          <Typography variant="body2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {displayValue && displayValue !== '—' && String(displayValue).trim() ? displayValue : '—'}
          </Typography>
        </>
      );
    }

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        {isLink && displayValue !== '—' ? (
          <MuiLink
            href={displayValue}
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            sx={{
              wordBreak: 'break-all',
              color: 'secondary.main',
              textDecorationColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                color: 'primary.main',
                textDecorationColor: (theme) => theme.palette.primary.main,
              }
            }}
          >
            {displayValue}
          </MuiLink>
        ) : (
          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {displayValue}
          </Typography>
        )}
      </Box>
    );
  }

  // Edit mode
  return (
    <TextField
      label={label}
      value={value || ''}
      fullWidth
      required={required}
      type={type}
      multiline={multiline}
      minRows={minRows}
      maxRows={maxRows}
      onChange={onChange}
      variant="outlined"
      error={!!error}
      helperText={error || helperText || ''}
      InputLabelProps={{
        sx: { color: 'primary.main', fontWeight: 600 }
      }}
    />
  );
};

const ClientFormFields = ({ data, editable, onChange, errors = {}, displayName, projectId, isCreating = false }) => {
  const gridSpacing = editable ? 3 : 4;
  const gridBreakpoint = editable ? 'sm' : 'md';

  const [industrySectors, setIndustrySectors] = useState([]);

  useEffect(() => {
    const fetchIndustrySectors = async () => {
      try {
        const response = await fetch('/hart/industry_sectors', {
          credentials: 'include',
        });
        if (response.ok) {
          const sectors = await response.json();
          setIndustrySectors(sectors || []);
        }
      } catch (error) {
        console.error('Error fetching industry sectors:', error);
      }
    };
    fetchIndustrySectors();
  }, []);

  const findMatchingSector = (value) => {
    if (!value) return '';
    if (typeof value === 'object' && value.name) return value.name;
    const match = industrySectors.find(
      (s) => s.name && s.name.toLowerCase() === String(value).toLowerCase()
    );
    return match ? match.name : value;
  };

  const activeSectors = industrySectors.filter((s) => !s.inactive);

  const fieldEditable = (fieldName) => {
    return editable && canEditField(fieldName, 'client', projectId);
  };

  return (
    <Box component={editable ? "form" : "div"} sx={{ mt: 0, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h2" sx={{ mb: 0 }}>
          {displayName} Details
        </Typography>
        <StatusField 
          data={data} 
          editable={fieldEditable('status')} 
          onChange={onChange}
          endpoint="/hart/client_statuses"
        />
      </Box>

      <Grid container spacing={gridSpacing}>
        {/* Row 1: Org ID (if not creating) | Org Name */}
        {!isCreating && (
          <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
            <FormField
              label="Organization ID"
              value={data.org_id}
              editable={false}
              onChange={() => { }}
              helperText="System generated"
            />
          </Grid>
        )}
        <Grid item xs={12} {...{ [gridBreakpoint]: isCreating ? 12 : 6 }}>
          <FormField
            label="Organization Name"
            value={data.name}
            editable={fieldEditable('name')}
            onChange={(e) => onChange({ ...data, name: e.target.value })}
            error={errors.name}
            required
          />
        </Grid>

        {/* Row 2: Org Address | Org Website */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Organization Address"
            value={data.org_address}
            editable={fieldEditable('org_address')}
            onChange={(e) => onChange({ ...data, org_address: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Organization Website"
            value={data.org_website}
            editable={fieldEditable('org_website')}
            onChange={(e) => onChange({ ...data, org_website: e.target.value })}
            type="url"
            error={errors.org_website}
            helperText="Enter a valid URL (e.g., https://example.com)"
            isLink
          />
        </Grid>

        {/* Row 3: Org Phone | Sector */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Organization Phone"
            value={data.org_phone}
            editable={fieldEditable('org_phone')}
            onChange={(e) => onChange({ ...data, org_phone: e.target.value })}
            type="tel"
            error={errors.org_phone}
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          {fieldEditable('sector') ? (
            <FormControl fullWidth error={!!errors.sector}>
              <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Sector</InputLabel>
              <Select
                value={findMatchingSector(data.sector) || ''}
                onChange={(e) => onChange({ ...data, sector: e.target.value || '' })}
                label="Sector"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {activeSectors.map((sector) => (
                  <MenuItem key={sector.name} value={sector.name}>
                    {sector.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.sector && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.sector}
                </Typography>
              )}
            </FormControl>
          ) : (
            <FormField
              label="Sector"
              value={typeof data.sector === 'object' && data.sector?.name ? data.sector.name : data.sector}
              editable={false}
            />
          )}
        </Grid>

        {/* Row 4: Year Started | Parent Organization */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Year Started"
            value={data.year_started}
            editable={fieldEditable('year_started')}
            onChange={(e) => onChange({ ...data, year_started: e.target.value })}
            error={errors.year_started}
            type="number"
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Parent Organization"
            value={data.parent_client}
            editable={fieldEditable('parent_client')}
            onChange={(e) => onChange({ ...data, parent_client: e.target.value })}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

ClientFormFields.propTypes = {
  data: PropTypes.object.isRequired,
  editable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  displayName: PropTypes.string,
  projectId: PropTypes.string,
  isCreating: PropTypes.bool,
};

export default ClientFormFields;
